import { useState, useEffect, useRef } from 'react';

const API = import.meta.env.VITE_API_URL || '/api';

interface Product {
  id: string;
  name: string;
  salePrice: number | string;
  categoryId?: string | null;
  unit?: string;
  presentations?: { side: 'sale' | 'purchase'; name: string; factor: string | number; price?: string | number | null }[];
}

interface CartItem {
  productId: string;
  name: string;
  unitPrice: number;
  quantity: number;
  maxStock: number;
  presentationName?: string;
  presentationFactor?: number;
}

interface Customer {
  id: string;
  name: string;
  taxId?: string;
  creditEnabled?: boolean;
  creditLimit?: number | string;
}

interface PosRegister {
  id: string;
  name: string;
  alias?: string;
  type?: string;
  currentBalance?: string | number;
  isPos?: boolean;
}

interface CardTerminal {
  id: string;
  name: string;
}

interface Sale {
  id: string;
  total: number | string;
  paymentMethod: string;
  invoiced?: boolean;
  remitoGenerated?: boolean;
  invoiceId?: string | null;
  invoiceType?: string | null;
}

export function POS() {
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<{ id: string; name: string }[]>([]);
  const [catFilter, setCatFilter] = useState('');
  const [stockMap, setStockMap] = useState<Record<string, number>>({});
  const [cart, setCart] = useState<CartItem[]>([]);
  const [search, setSearch] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [cashReceived, setCashReceived] = useState('');
  const [discountInput, setDiscountInput] = useState('');
  const [lastSale, setLastSale] = useState<Sale | null>(null);
  const [error, setError] = useState('');
  const [noStockWarning, setNoStockWarning] = useState('');
  // Producto activo esperando elección de presentación (ej: docena, maple)
  const [pendingPresentationFor, setPendingPresentationFor] = useState<any>(null);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(null);
  const [selectedCustomerCredit, setSelectedCustomerCredit] = useState(false);
  const [customerQuery, setCustomerQuery] = useState('');
  const [showCustomerResults, setShowCustomerResults] = useState(false);
  const [posRegisters, setPosRegisters] = useState<PosRegister[]>([]);
  // Cajas físicas (efectivo) — para abrir la caja
  const [cashDrawers, setCashDrawers] = useState<PosRegister[]>([]);
  // Cajas físicas con turno abierto (por registerId — no global)
  const [openDrawerIds, setOpenDrawerIds] = useState<string[]>([]);
  // Detalle de cajas abiertas: quién la abrió + efectivo disponible
  const [openDrawerInfo, setOpenDrawerInfo] = useState<{ registerId: string; openedByName?: string; openingBalance?: string; currentBalance?: string }[]>([]);
  const [openShiftsLoaded, setOpenShiftsLoaded] = useState(false);
  const [selectedRegisterId, setSelectedRegisterId] = useState<string | null>(null);
  const [cardTerminals, setCardTerminals] = useState<CardTerminal[]>([]);
  const [selectedTerminalId, setSelectedTerminalId] = useState<string | null>(null);
  const [cardType, setCardType] = useState<'debit' | 'credit'>('credit');
  const [wantsInvoice, setWantsInvoice] = useState(false);
  const [loading, setLoading] = useState(false);
  const barcodeRef = useRef<HTMLInputElement>(null);
  const token = localStorage.getItem('accessToken');
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'x-tenant-slug': localStorage.getItem('tenantSlug') || '' };

  // New state for open shift detection and modal
  const [openShiftModal, setOpenShiftModal] = useState(false);
  const [openShiftForm, setOpenShiftForm] = useState({ registerId: '', openingBalance: '', note: '' });
  const [noOpenShift, setNoOpenShift] = useState(false);

  async function loadProducts() {
    try {
      const res = await fetch(`${API}/products?q=${search}`, { headers });
      if (res.ok) { const d = await res.json(); setProducts(d.data || []); }
      else { const err = await res.json(); throw new Error(err.error || 'Error al cargar productos'); }
    } catch (e: any) {
      setError(e.message ?? 'Error al cargar productos');
    }
  }

  async function loadStaticData() {
    try {
      const [stockRes, posRes, drawersRes, termRes, catRes] = await Promise.all([
        fetch(`${API}/stock/levels`, { headers }),
        fetch(`${API}/cash/registers/pos-enabled`, { headers }),
        fetch(`${API}/cash/registers?type=cash_drawer`, { headers }),
        fetch(`${API}/cash/card-terminals`, { headers }),
        fetch(`${API}/categories`, { headers }),
      ]);
      if (catRes.ok) { const cd = await catRes.json(); setCategories(cd.data || []); }
      if (stockRes.ok) {
        const d = await stockRes.json();
        const map: Record<string, number> = {};
        for (const s of (d.data || [])) map[s.productId] = Number(s.quantity);
        setStockMap(map);
      } else { const err = await stockRes.json(); throw new Error(err.error || 'Error al cargar stock'); }
      if (posRes.ok) { const d = await posRes.json(); setPosRegisters(d.data || []); }
      else { const err = await posRes.json(); throw new Error(err.error || 'Error al cargar cajas'); }
      // Cajas físicas (efectivo) — las únicas que se abren con arqueo
      if (drawersRes.ok) { const d = await drawersRes.json(); setCashDrawers(d.data || []); }
      if (termRes.ok) { const d = await termRes.json(); setCardTerminals(d.data || []); }
      else { const err = await termRes.json(); throw new Error(err.error || 'Error al cargar terminales'); }
    } catch (e: any) {
      setError(e.message ?? 'Error al cargar datos del POS');
    }
  }

  // Load open shifts (per drawer) after registers are loaded
  useEffect(() => {
    async function checkOpenShift() {
      if (cashDrawers.length === 0) return;
      try {
        const res = await fetch(`${API}/cash/shifts`, { headers });
        if (res.ok) {
          const data = await res.json();
          const shifts = data.data || [];
          // Cajas con turno abierto — por registerId, no global
          const openShifts = shifts.filter((s: any) => s.status === 'open');
          const openIds = openShifts.map((s: any) => s.registerId);
          setOpenDrawerIds(openIds);
          // Detalle para mostrar quién la abrió + efectivo disponible
          setOpenDrawerInfo(openShifts.map((s: any) => {
            const drawer = cashDrawers.find((d) => d.id === s.registerId);
            return {
              registerId: s.registerId,
              openedByName: s.openedByName || s.openedByEmail,
              openingBalance: s.openingBalance,
              currentBalance: drawer?.currentBalance != null ? String(drawer.currentBalance) : undefined,
            };
          }));
          // noOpenShift: true si NINGUNA caja física está abierta (banner de acción)
          const drawerIds = cashDrawers.map(d => d.id);
          const anyOpen = drawerIds.some(id => openIds.includes(id));
          setNoOpenShift(!anyOpen);
          setOpenShiftsLoaded(true);
        } else {
          setNoOpenShift(true);
          setOpenShiftsLoaded(true);
        }
      } catch (e) {
        console.error('Failed to check open shift:', e);
        setNoOpenShift(true);
        setOpenShiftsLoaded(true);
      }
    }
    checkOpenShift();
  }, [cashDrawers, headers]);

  // Lazy load customers on search (was: load ALL on mount)
  async function searchCustomers(query: string) {
    if (query.length < 2) { setCustomers([]); return; }
    try {
      const res = await fetch(`${API}/customers?q=${query}`, { headers });
      if (res.ok) { const d = await res.json(); setCustomers(d.data || []); }
      else { const err = await res.json(); throw new Error(err.error || 'Error al buscar clientes'); }
    } catch (e: any) {
      setError(e.message ?? 'Error al buscar clientes');
    }
  }

  // Load static data once on mount
  useEffect(() => { loadStaticData(); }, []);

  // Debounced product search — 300ms
  useEffect(() => {
    const t = setTimeout(() => loadProducts(), 300);
    return () => clearTimeout(t);
  }, [search]);

  // Auto-focus barcode scanner on mount
  useEffect(() => { barcodeRef.current?.focus(); }, []);

  // Open shift modal with F8 shortcut
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'F8' || e.key === 'f8') {
        e.preventDefault();
        // If no open shift, open modal to open one
        if (noOpenShift) {
          // Set default register to first available cash drawer
          if (cashDrawers.length > 0) {
            setOpenShiftForm({ registerId: cashDrawers[0].id, openingBalance: '', note: '' });
          }
          setOpenShiftModal(true);
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [noOpenShift, cashDrawers]);

  const subtotal = cart.reduce((sum, item) => sum + Number(item.unitPrice) * item.quantity, 0);

  // Smart discount: if input ends with %, it's percentage; if starts with $, it's fixed; else auto-detect
  const discountValue = parseFloat(discountInput.replace('%', '').replace('$', '')) || 0;
  const isPercentage = discountInput.includes('%') || (!discountInput.includes('$') && discountValue <= 100 && discountValue > 0 && subtotal > 0 && (discountValue / subtotal) < 0.5);
  const discountAmount = isPercentage
    ? subtotal * (discountValue / 100)
    : Math.min(discountValue, subtotal);
  const total = Math.max(0, subtotal - discountAmount);

  function getStock(productId: string): number {
    return stockMap[productId] ?? 0;
  }

  // Elegir presentación si el producto tiene (ej: docena, maple); si no, agrega directo.
  function addWithPresentation(product: any, presentation?: { name: string; factor: string | number; price?: string | number | null }) {
    const stock = getStock(product.id);
    const salePresentations = (product.presentations || []).filter((p: any) => p.side === 'sale');

    if (presentation) {
      const factor = Number(presentation.factor) || 1;
      // Stock disponible expresado en esta presentación (unidad base / factor)
      const stockInPres = Math.floor((stock * 100) / (factor * 100));
      setCart([...cart, {
        productId: product.id,
        name: product.name,
        unitPrice: Number(presentation.price != null && presentation.price !== '' ? presentation.price : product.salePrice),
        quantity: 1,
        maxStock: stockInPres,
        presentationName: presentation.name,
        presentationFactor: factor,
      }]);
      return;
    }

    // Sin presentaciones: comportamiento actual (+1 con stock normal)
    const inCart = cart.find(i => i.productId === product.id);
    const currentQty = inCart ? inCart.quantity : 0;

    if (currentQty >= stock) {
      setNoStockWarning(`Sin stock suficiente de ${product.name}. Disponible: ${stock}`);
      setTimeout(() => setNoStockWarning(''), 3000);
      return;
    }

    if (inCart) {
      setCart(cart.map(i => i.productId === product.id ? { ...i, quantity: i.quantity + 1 } : i));
    } else {
      if (stock <= 0) {
        setNoStockWarning(`${product.name} sin stock`);
        setTimeout(() => setNoStockWarning(''), 3000);
        return;
      }
      setCart([...cart, { productId: product.id, name: product.name, unitPrice: Number(product.salePrice), quantity: 1, maxStock: stock }]);
    }
  }

  function addToCart(product: any) {
    const salePresentations = (product.presentations || []).filter((p: any) => p.side === 'sale');
    if (salePresentations.length > 0) {
      // Con presentaciones: dejar que el tile la elija (ver JSX). El tile llama addWithPresentation.
      setPendingPresentationFor(product);
      return;
    }
    addWithPresentation(product, undefined);
  }

  function updateQty(idx: number, delta: number) {
    const newCart = [...cart];
    const newQty = newCart[idx].quantity + delta;
    if (newQty < 1) return;
    if (newQty > newCart[idx].maxStock) {
      setNoStockWarning(`Solo quedan ${newCart[idx].maxStock} de ${newCart[idx].name}`);
      setTimeout(() => setNoStockWarning(''), 3000);
      return;
    }
    newCart[idx].quantity = newQty;
    setCart(newCart);
  }

  function removeFromCart(idx: number) {
    setCart(cart.filter((_, i) => i !== idx));
  }

  // Customer search — debounced lazy load from API
  useEffect(() => {
    const t = setTimeout(() => searchCustomers(customerQuery), 250);
    return () => clearTimeout(t);
  }, [customerQuery]);

  const filteredCustomers = customers; // already filtered by API

  function selectCustomer(id: string, name: string, creditEnabled = false) {
    setSelectedCustomerId(id);
    setSelectedCustomerCredit(creditEnabled);
    setCustomerQuery(name);
    setShowCustomerResults(false);
    // Si el cliente no tiene CC y estaba seleccionado el pago a cuenta, volver a efectivo
    if (!creditEnabled && paymentMethod === 'credit') setPaymentMethod('cash');
  }

  async function checkout() {
    setError('');
    try {
      const body: any = {
        items: cart.map(i => ({
          productId: i.productId,
          quantity: i.quantity,
          presentationName: i.presentationName || undefined,
          presentationFactor: i.presentationFactor || undefined,
        })),
        paymentMethod,
        cashReceived: paymentMethod === 'cash' ? cashReceived : undefined,
        invoice: wantsInvoice,
        customerId: selectedCustomerId || undefined,
      };
      if (paymentMethod !== 'cash' && selectedRegisterId) body.registerId = selectedRegisterId;
      if (paymentMethod === 'card' && selectedTerminalId) {
        body.terminalId = selectedTerminalId;
        body.cardType = cardType;
      }
      if (discountAmount > 0) {
        body.discount = discountAmount.toFixed(2);
        body.discountType = isPercentage ? 'percentage' : 'fixed';
      }

      const res = await fetch(`${API}/sales`, {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e.error || 'Error'); }
      const data = await res.json();
      setLastSale({ ...data.data, invoiced: data.invoiced, remitoGenerated: !data.invoiced, invoiceId: data.invoiceId || null, invoiceType: data.invoiceType || null });
      setCart([]);
      setCashReceived('');
      setDiscountInput('');
      setSelectedCustomerId(null);
      setSelectedCustomerCredit(false);
      setCustomerQuery('');
      setWantsInvoice(false);
      // Re-focus barcode after sale
      barcodeRef.current?.focus();
    } catch (e: any) {
      setError(e.message);
    }
  }

  const change = paymentMethod === 'cash' && cashReceived ? Number(cashReceived) - total : 0;
  const canCheckout = cart.length > 0
    && !(paymentMethod === 'cash' && cashReceived && change < 0)
    && !(paymentMethod === 'credit' && !selectedCustomerId);

  const bankButtons = posRegisters.map(r => ({
    label: r.alias || r.name.replace('Banco ', '').replace('Cuenta ', ''),
    registerId: r.id,
  }));

  const terminalButtons = cardTerminals.map(t => ({
    label: t.name,
    terminalId: t.id,
  }));

  // Function to open shift via API
  const handleOpenShift = async () => {
    if (!openShiftForm.registerId || !openShiftForm.openingBalance) return;
    try {
      const res = await fetch(`${API}/cash/shifts/open`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          registerId: openShiftForm.registerId,
          openingBalance: parseFloat(openShiftForm.openingBalance),
          note: openShiftForm.note,
        }),
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e.error || 'Error al abrir la caja'); }
      // Success: close modal and refresh open shift status
      setOpenShiftModal(false);
      setOpenShiftForm({ registerId: '', openingBalance: '', note: '' });
      // Trigger a re-check
      setNoOpenShift(true); // will be corrected by effect
    } catch (err: any) {
      setError(err.message ?? 'Error inesperado');
    }
  };

  async function downloadInvoicePdf(invoiceId: string, type?: string | null) {
    try {
      const res = await fetch(`${API}/invoices/${invoiceId}/pdf`, { headers });
      if (!res.ok) throw new Error('Error al descargar el PDF');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${type === 'remito' ? 'remito' : 'factura'}-${invoiceId.slice(0, 8)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e: any) {
      setError(e.message ?? 'Error al descargar el PDF');
    }
  }

  return (
    <div className="p-3 sm:p-4 lg:p-6 max-w-7xl mx-auto">
      {/* Alerts */}
      {error && <div className="bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 p-2 rounded mb-2 text-sm">{error}</div>}
      {noStockWarning && <div className="bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300 p-2 rounded mb-2 text-sm font-medium">⚠️ {noStockWarning}</div>}
      {lastSale && (
        <div className="bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 p-2 rounded mb-2 text-sm flex flex-wrap justify-between items-center gap-2">
          <span>✓ Venta #{lastSale.id?.slice(0, 8)} — ${lastSale.total} ({lastSale.paymentMethod})
            {lastSale.invoiced ? ' • 🧾 Factura' : lastSale.remitoGenerated ? ' • 📋 Remito generado' : ''}
            {lastSale.invoiceId && (
              <button
                onClick={(e) => { e.preventDefault(); downloadInvoicePdf(lastSale.invoiceId!, lastSale.invoiceType); }}
                className="ml-2 px-3 py-1 bg-green-600 hover:bg-green-700 text-white rounded-lg text-xs font-medium min-h-[36px]"
              >
                ⬇️ Descargar PDF
              </button>
            )}
          </span>
          <button onClick={() => setLastSale(null)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>✕</button>
        </div>
      )}
      {/* Banner for no open shift */}
      {noOpenShift && (
        <div style={{ background: '#fffbeb', borderLeft: '4px solid #f59e0b', padding: '12px', marginBottom: '12px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>No hay caja abierta. </span>
            <button onClick={() => {
              // Set default register to first available cash drawer
              if (cashDrawers.length > 0) {
                setOpenShiftForm({ registerId: cashDrawers[0].id, openingBalance: '', note: '' });
              }
              setOpenShiftModal(true);
            }} style={{ padding: '6px 12px', background: '#2563eb', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
              Abrir caja
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-3">
        {/* LEFT: Products + Scanner — 3/5 width */}
        <div className="lg:col-span-3">
          {/* Barcode scanner — always visible */}
          <input
            ref={barcodeRef}
            autoFocus
            type="text"
            placeholder="📷 Escaneá o buscá un producto..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full px-4 py-3 border-2 border-blue-500 rounded mb-2 text-base"
            onKeyDown={async (e) => {
              if (e.key === 'Enter' && search.length >= 3) {
                // Try barcode first
                try {
                  const res = await fetch(`${API}/products/barcode/${search}`, { headers });
                  if (res.ok) {
                    const data = await res.json();
                    addToCart(data.data);
                    setSearch('');
                  } else {
                    const err = await res.json();
                    setError(err.error || 'Producto no encontrado');
                  }
                } catch (e: any) {
                  setError(e.message ?? 'Error al buscar producto');
                }
              }
            }}
          />

          {/* Product grid — compact */}
          <div className="flex flex-wrap gap-1.5 mb-2" style={{ display: categories.length ? 'flex' : 'none' }}>
            <button
              key="all"
              onClick={() => setCatFilter('')}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border min-h-[36px] transition-colors ${
                catFilter === ''
                  ? 'bg-purple-600 border-purple-600 text-white'
                  : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200'
              }`}
            >
              Todas
            </button>
            {categories.map((c) => (
              <button
                key={c.id}
                onClick={() => setCatFilter(catFilter === c.id ? '' : c.id)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold border min-h-[36px] transition-colors ${
                  catFilter === c.id
                    ? 'bg-purple-600 border-purple-600 text-white'
                    : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200'
                }`}
              >
                {c.name}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
            {(catFilter ? products.filter(p => p.categoryId === catFilter) : products).map(p => {
              const stock = getStock(p.id);
              const outOfStock = stock <= 0;
              const hasSalePres = (p.presentations || []).some((pp: any) => pp.side === 'sale');
              return (
                <button
                  key={p.id}
                  onClick={() => addToCart(p)}
                  disabled={outOfStock}
                  className="min-h-[44px]"
                  style={{
                    padding: '8px', borderRadius: '6px', textAlign: 'left', cursor: outOfStock ? 'not-allowed' : 'pointer',
                    border: hasSalePres ? '1px solid #c084fc' : '1px solid #e5e7eb',
                    background: outOfStock ? '#f9fafb' : '#fff', opacity: outOfStock ? 0.5 : 1,
                    transition: 'all 0.15s',
                  }}
                  onMouseDown={e => { if (!outOfStock) e.currentTarget.style.background = '#eff6ff'; }}
                  onMouseUp={e => { e.currentTarget.style.background = outOfStock ? '#f9fafb' : '#fff'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = outOfStock ? '#f9fafb' : '#fff'; }}
                >
                  <p style={{ fontSize: '13px', fontWeight: 600, color: '#1f2937', margin: 0, lineHeight: '1.2', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</p>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '4px' }}>
                    <span style={{ fontSize: '15px', fontWeight: 700, color: '#059669' }}>${p.salePrice}</span>
                    <span style={{
                      fontSize: '10px', padding: '1px 6px', borderRadius: '8px',
                      background: outOfStock ? '#fee2e2' : stock <= 5 ? '#fef3c7' : '#d1fae5',
                      color: outOfStock ? '#991b1b' : stock <= 5 ? '#92400e' : '#065f46',
                    }}>
                      {outOfStock ? 'S/s' : `${stock}u`}
                    </span>
                  </div>
                  {hasSalePres && (
                    <p style={{ fontSize: '10px', color: '#7e22ce', margin: '3px 0 0', fontWeight: 600 }}>
                      🥚 {(p.presentations || []).filter((pp: any) => pp.side === 'sale').map((pp: any) => pp.name).join(' · ')}
                    </p>
                  )}
                </button>
              );
            })}
            {products.length === 0 && (
              <p style={{ color: '#9ca3af', gridColumn: '1/-1', textAlign: 'center', padding: '20px', fontSize: '14px' }}>
                {search ? 'Sin resultados' : 'Escribí para buscar...'}
              </p>
            )}
          </div>
        </div>

        {/* RIGHT: Cart + Checkout — 2/5 width */}
        <div className="lg:col-span-2 bg-white dark:bg-gray-800" style={{ borderRadius: '8px', padding: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', display: 'flex', flexDirection: 'column', maxHeight: '85vh' }}>
          {/* Cart header with count */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
            <h2 style={{ fontSize: '16px', fontWeight: 700, margin: 0 }}>🛒 {cart.length} {cart.length === 1 ? 'item' : 'items'}</h2>
            {cart.length > 0 && (
              <button onClick={() => setCart([])} style={{ fontSize: '12px', color: '#dc2626', background: 'none', border: 'none', cursor: 'pointer' }}>Vaciar</button>
            )}
          </div>

          {/* Customer — inline autocomplete */}
          <div style={{ marginBottom: '8px', position: 'relative' }}>
            <input
              placeholder="👤 Cliente (Consumidor Final)"
              value={customerQuery}
              onChange={e => { setCustomerQuery(e.target.value); setShowCustomerResults(true); }}
              onFocus={() => setShowCustomerResults(true)}
              onBlur={() => setTimeout(() => setShowCustomerResults(false), 200)}
              className="w-full px-3 py-2 border rounded text-sm"
              style={selectedCustomerId ? { borderColor: '#10b981' } : undefined}
            />
            {showCustomerResults && filteredCustomers.length > 0 && (
              <div className="border border-gray-300 dark:border-gray-600 dark:bg-gray-800" style={{ position: 'absolute', zIndex: 50, borderRadius: '6px', width: '100%', maxHeight: '200px', overflowY: 'auto', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}>
                {filteredCustomers.slice(0, 8).map(c => (
                  <button
                    key={c.id}
                    onMouseDown={() => selectCustomer(c.id, c.name, c.creditEnabled)}
                    className="block w-full text-left dark:text-gray-100 dark:border-gray-700"
                    style={{ padding: '8px 12px', background: 'none', cursor: 'pointer', fontSize: '13px', borderBottom: '1px solid #f3f4f6' }}
                  >
                    {c.name} {c.taxId && <span style={{ color: '#9ca3af', fontSize: '11px' }}>({c.taxId})</span>}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Cart items — scrollable */}
          <div style={{ flex: 1, overflowY: 'auto', marginBottom: '8px' }}>
            {cart.length === 0 ? (
              <p style={{ color: '#9ca3af', textAlign: 'center', padding: '20px', fontSize: '14px' }}>Tocá un producto para agregar</p>
            ) : (
              <div>
                {cart.map((item, idx) => (
                  <div key={idx} className="bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 8px', borderRadius: '6px', marginBottom: '4px' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <span style={{ display: 'block', fontSize: '13px', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.name}</span>
                      {item.presentationName && (
                        <span style={{ display: 'block', fontSize: '10px', color: '#7e22ce', fontWeight: 600 }}>🥚 {item.presentationName} ×{item.presentationFactor ?? 1} u.</span>
                      )}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <button onClick={() => updateQty(idx, -1)} className="min-h-[44px] min-w-[44px] flex items-center justify-center" style={{ padding: '2px 8px', fontSize: '16px', border: 'none', background: 'none', cursor: 'pointer', color: '#6b7280' }}>−</button>
                      <span style={{ width: '24px', textAlign: 'center', fontSize: '14px', fontWeight: 600 }}>{item.quantity}</span>
                      <button onClick={() => updateQty(idx, 1)} className="min-h-[44px] min-w-[44px] flex items-center justify-center" style={{ padding: '2px 8px', fontSize: '16px', border: 'none', background: 'none', cursor: 'pointer', color: '#6b7280' }}>+</button>
                      <span style={{ width: '60px', textAlign: 'right', fontSize: '13px', fontWeight: 600 }}>${(item.unitPrice * item.quantity).toFixed(0)}</span>
                      <button onClick={() => removeFromCart(idx)} className="min-h-[44px] min-w-[44px] flex items-center justify-center" style={{ padding: '2px 6px', border: 'none', background: 'none', cursor: 'pointer', color: '#dc2626', fontSize: '14px' }}>✕</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Bottom section: discount + total + payment + checkout */}
          {cart.length > 0 && (
            <div className="border-t-2 border-gray-200 dark:border-gray-700" style={{ paddingTop: '8px' }}>
              {/* Smart discount — single input */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '8px' }}>
                <span style={{ fontSize: '13px', color: '#6b7280', fontWeight: 500 }}>Descuento:</span>
                <input
                  type="text"
                  placeholder="0"
                  value={discountInput}
                  onChange={e => setDiscountInput(e.target.value)}
                  className="w-24 px-2 py-1 border rounded text-sm"
                />
                {discountAmount > 0 && (
                  <span style={{ fontSize: '12px', color: '#dc2626', fontWeight: 600 }}>
                    −${discountAmount.toFixed(0)}{isPercentage ? ` (${discountValue}%)` : ''}
                  </span>
                )}
              </div>

              {/* Total */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <span style={{ fontSize: '18px', fontWeight: 700 }}>Total:</span>
                <span style={{ fontSize: '24px', fontWeight: 800, color: '#059669' }}>${total.toFixed(0)}</span>
              </div>

              {/* Payment method — compact buttons */}
              <div style={{ marginBottom: '6px' }}>
                <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                  <button
                    onClick={() => { setPaymentMethod('cash'); setSelectedRegisterId(null); setSelectedTerminalId(null); }}
                    className={`min-h-[44px] px-3 py-1.5 rounded-md text-[13px] font-semibold cursor-pointer border-2 transition-colors ${
                      paymentMethod === 'cash'
                        ? 'bg-blue-600 border-blue-600 text-white'
                        : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200'
                    }`}
                  >💵 Efectivo</button>
                  {selectedCustomerCredit && (
                    <button
                      onClick={() => { setPaymentMethod('credit'); setSelectedRegisterId(null); setSelectedTerminalId(null); }}
                      className={`min-h-[44px] px-3 py-1.5 rounded-md text-[13px] font-semibold cursor-pointer border-2 transition-colors ${
                        paymentMethod === 'credit'
                          ? 'bg-purple-600 border-purple-600 text-white'
                          : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200'
                      }`}
                      title="Cargar a la cuenta corriente del cliente"
                    >📒 Cuenta Corriente</button>
                  )}
                  {bankButtons.map(b => (
                    <button
                      key={b.registerId}
                      onClick={() => { setPaymentMethod('transfer'); setSelectedRegisterId(b.registerId); setSelectedTerminalId(null); }}
                      className={`min-h-[44px] px-3 py-1.5 rounded-md text-[13px] font-semibold cursor-pointer border-2 transition-colors ${
                        paymentMethod === 'transfer' && selectedRegisterId === b.registerId
                          ? 'bg-violet-600 border-violet-600 text-white'
                          : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200'
                      }`}
                    >🏦 {b.label}</button>
                  ))}
                  {terminalButtons.map(t => (
                    <button
                      key={t.terminalId}
                      onClick={() => { setPaymentMethod('card'); setSelectedRegisterId(null); setSelectedTerminalId(t.terminalId); }}
                      className={`min-h-[44px] px-3 py-1.5 rounded-md text-[13px] font-semibold cursor-pointer border-2 transition-colors ${
                        paymentMethod === 'card' && selectedTerminalId === t.terminalId
                          ? 'bg-emerald-600 border-emerald-600 text-white'
                          : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200'
                      }`}
                    >💳 {t.label}</button>
                  ))}
                </div>
                {/* Card type — inline, compact */}
                {paymentMethod === 'card' && selectedTerminalId && (
                  <div style={{ display: 'flex', gap: '4px', marginTop: '4px' }}>
                    {['credit', 'debit'].map(ct => (
                      <button
                        key={ct}
                        onClick={() => setCardType(ct as 'debit' | 'credit')}
                        className={`px-2.5 py-1 rounded text-[11px] font-semibold cursor-pointer border-2 transition-colors ${
                          cardType === ct
                            ? 'bg-emerald-600 border-emerald-600 text-white'
                            : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200'
                        }`}
                      >{ct === 'credit' ? 'Crédito' : 'Débito'}</button>
                    ))}
                  </div>
                )}
              </div>

              {/* Cash received — only for cash */}
              {paymentMethod === 'cash' && (
                <div style={{ marginBottom: '6px' }}>
                  <input
                    placeholder="Efectivo recibido..."
                    type="number"
                    step="0.01"
                    value={cashReceived}
                    onChange={e => setCashReceived(e.target.value)}
                    className="w-full px-3 py-2 border rounded text-sm"
                  />
                  {cashReceived && change > 0 && (
                    <p style={{ fontSize: '14px', color: '#059669', marginTop: '4px', fontWeight: 700 }}>Cambio: ${change.toFixed(2)}</p>
                  )}
                  {cashReceived && change < 0 && (
                    <p style={{ fontSize: '14px', color: '#dc2626', marginTop: '4px', fontWeight: 700 }}>⚠️ Faltan ${Math.abs(change).toFixed(2)}</p>
                  )}
                </div>
              )}

              {/* Invoice checkbox + Checkout button */}
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '4px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '13px', cursor: 'pointer' }}>
                  <input type="checkbox" checked={wantsInvoice} onChange={e => setWantsInvoice(e.target.checked)} style={{ width: '18px', height: '18px', cursor: 'pointer' }} />
                  🧾 Facturar {!wantsInvoice && <span style={{ color: '#6b7280', fontSize: '11px' }}>(sin facturar = remito)</span>}
                </label>
              </div>
              <button
                onClick={checkout}
                disabled={!canCheckout}
                className={canCheckout ? 'bg-green-600 hover:bg-green-700' : 'bg-gray-300 dark:bg-gray-600'}
                style={{
                  width: '100%', padding: '14px', borderRadius: '8px', fontWeight: 700, fontSize: '18px', cursor: canCheckout ? 'pointer' : 'not-allowed',
                  border: 'none', color: '#fff', opacity: canCheckout ? 1 : 0.7,
                }}
              >
                {wantsInvoice ? '🧾' : '✓'} Cobrar ${total.toFixed(0)}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Open Shift Modal */}
      {openShiftModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setOpenShiftModal(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-sm" onClick={e => e.stopPropagation()}>
            <div className="p-4 border-b">
              <h2 className="text-lg font-bold text-gray-800 dark:text-gray-100">🔓 Abrir Caja</h2>
            </div>
            <div className="p-4 space-y-3">
              <div>
                <label className="block text-sm font-medium mb-1">Caja física (efectivo)</label>
                <select
                  value={openShiftForm.registerId}
                  onChange={e => setOpenShiftForm({ ...openShiftForm, registerId: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg text-sm min-h-[44px]"
                >
                  <option value="">Seleccionar caja...</option>
                  {cashDrawers.map(r => {
                    const open = openDrawerIds.includes(r.id);
                    const opener = openDrawerInfo.find((od) => od.registerId === r.id)?.openedByName;
                    return (
                      <option key={r.id} value={r.id} disabled={open}>
                        {open ? `⛔ Ya abierta por ${opener || 'otro usuario'} · ` : ''}{r.name}{r.isPos ? ' 🛒' : ''} — Efectivo disponible: ${Number(r.currentBalance || 0).toLocaleString('es-AR')}
                      </option>
                    );
                  })}
                </select>
                {cashDrawers.length === 0 && (
                  <p className="text-xs text-amber-600 mt-1">No hay cajas físicas configuradas.</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Fondo/arqueo de apertura ($)</label>
                <input
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  value={openShiftForm.openingBalance}
                  onChange={e => setOpenShiftForm({ ...openShiftForm, openingBalance: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg text-sm min-h-[44px]"
                />
                <p className="text-xs text-gray-500 mt-1">Contá el efectivo real en la caja y cargalo acá (arqueo de apertura).</p>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Nota (opcional)</label>
                <input
                  type="text"
                  placeholder="Nota"
                  value={openShiftForm.note}
                  onChange={e => setOpenShiftForm({ ...openShiftForm, note: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg text-sm min-h-[44px]"
                />
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  onClick={handleOpenShift}
                  disabled={!openShiftForm.registerId || !openShiftForm.openingBalance}
                  className="flex-1 px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white rounded-lg font-medium min-h-[44px] transition-colors"
                >
                  Abrir
                </button>
                <button
                  onClick={() => setOpenShiftModal(false)}
                  className="flex-1 px-4 py-2 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 text-gray-700 dark:text-gray-200 rounded-lg font-medium min-h-[44px] transition-colors"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    {/* Modal: elegir presentación (ej: 1/2 docena, docena, maple) */}
      {pendingPresentationFor && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
          onClick={() => setPendingPresentationFor(null)}
        >
          <div
            className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-sm w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-bold mb-1">🥚 {pendingPresentationFor.name}</h3>
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
              Elegí la presentación para agregar al carrito
            </p>
            <div className="flex flex-col gap-2 mb-4">
              {(pendingPresentationFor.presentations || [])
                .filter((p: any) => p.side === 'sale')
                .map((p: any) => (
                  <button
                    key={p.name}
                    onClick={() => {
                      addWithPresentation(pendingPresentationFor, p);
                      setPendingPresentationFor(null);
                    }}
                    className="flex items-center justify-between px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-blue-50 dark:hover:bg-gray-700 min-h-[44px] transition-colors"
                  >
                    <span className="font-medium">{p.name}</span>
                    <span className="text-sm text-gray-600 dark:text-gray-300">
                      ${Number(p.price ?? 0).toFixed(0)}
                    </span>
                  </button>
                ))}
            </div>
            <button
              onClick={() => setPendingPresentationFor(null)}
              className="w-full px-4 py-2 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg font-medium min-h-[44px]"
            >
              Cancelar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}